import { useEffect, useState } from 'react'
import { useRegion } from '../lib/region'
import { getAffiliateLinks } from '../data/affiliates'
import type { AccessoryLine } from '../data/companions'
import { fmtNum } from '../lib/format'
import { useLocale } from '../i18n/locale'
import { t } from '../i18n/strings'
import { unitLabel } from '../i18n/units'
import { useUnitSystem, convertQty, isImperialMeasurement } from '../lib/units'

export interface PrimaryResult {
  name: string // short label used in the buy list
  label: string // phrasing under the headline number
  qty: number
  unit: string
  query: string
}

interface Props {
  primary: PrimaryResult
  secondary?: PrimaryResult | null
  lines: AccessoryLine[]
  assumptions: string
  // Optional "How we got this" content. When provided, an ⓘ button appears
  // top-right of the headline; clicking expands an inline panel showing the
  // step-by-step math + actual input values + applied waste/coverage. Builds
  // trust by making every assumption visible.
  explain?: React.ReactNode
  // Save/Print/PDF/Share actions live in <CalcActions> on the left (form) column
  // now — moved out of ResultSheet so all four buttons fit on a single row.
}

// Lowercase only the FIRST character so a label reads naturally after "of …"
// ("Primer" → "primer") WITHOUT mangling mid-string unit casing like "4 L" or
// "100 sq ft" (a blanket .toLowerCase() turned "4 L" into "4 l").
const lowerFirst = (s: string) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s)

function OptionalChip() {
  const locale = useLocale()
  return <span className="ml-1 rounded bg-surface-2 px-1.5 py-0.5 text-[10px] font-medium text-text-subtle">{t(locale, 'result.optional')}</span>
}

function Row({ line, onRemove }: { line: AccessoryLine; onRemove?: () => void }) {
  const locale = useLocale()
  const [system] = useUnitSystem()
  // Convert only measurement units (ft, sq ft, cu yd, gal, lb...) — discrete
  // pack units like 'bag', 'roll', 'bundle', 'piece' pass through unchanged.
  const rawUnit = line.unit ?? ''
  const { qty: shownQty, unit: shownUnit } = isImperialMeasurement(rawUnit) && line.qty != null
    ? convertQty(line.qty, rawUnit, system)
    : { qty: line.qty ?? 0, unit: rawUnit }
  return (
    <div className="group flex items-start justify-between gap-2 py-2">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-text">
          {line.label}
          {line.kind === 'consumable' && line.qty != null && (
            <span className="tnum ml-2 text-brand-text">
              {fmtNum(shownQty, 2)} {unitLabel(shownUnit, shownQty, locale)}
            </span>
          )}
          {line.optional && <OptionalChip />}
        </div>
        {line.note && <div className="text-xs text-text-subtle">{line.note}</div>}
      </div>
      {/* Inline delete — non-destructive: deletion is local to this render and
          will reset the moment the calculator recomputes (any input change).
          Lets the user prune their buy list without losing the option. */}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={t(locale, 'result.removeLine')}
          title={t(locale, 'result.removeLine')}
          className="no-print mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded text-sm text-text-subtle opacity-0 transition-opacity hover:bg-danger/10 hover:text-danger focus:opacity-100 group-hover:opacity-100"
        >
          ×
        </button>
      )}
    </div>
  )
}

// Single consolidated, low-pressure shopping section at the very end.
function WhereToBuy({ items }: { items: { label: string; query: string }[] }) {
  const locale = useLocale()
  const [region] = useRegion()
  const stores = getAffiliateLinks('paint', region, locale)
  const [store, setStore] = useState(stores[0].name)
  const linkFor = (query: string) => getAffiliateLinks(query, region, locale).find((s) => s.name === store)?.url ?? '#'

  return (
    <div className="border-t border-border p-5">
      <h3 className="text-sm font-semibold text-text">{t(locale, 'result.whereToBuy')}</h3>
      <p className="mt-0.5 text-xs text-text-subtle">{t(locale, 'result.whereToBuyNote')}</p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {stores.map((s) => (
          <button
            key={s.name}
            onClick={() => setStore(s.name)}
            className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
              store === s.name ? 'border-brand bg-brand-tint/60' : 'border-border'
            }`}
            style={{ color: s.color }}
          >
            {s.name}
          </button>
        ))}
      </div>
      {/* Clean 2-column text grid. Faster to load, easier to maintain, and
          reads less "ad-farm" than the previous thumbnail grid. Links use the
          RenoSheets dark green (#166534) to match the logo's "Sheets" wordmark. */}
      <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
        {items.map((it, i) => (
          <li key={i}>
            <a
              href={linkFor(it.query)}
              target="_blank"
              rel="nofollow sponsored noopener"
              className="block underline-offset-2 hover:underline"
              style={{ color: '#166534' }}
            >
              {it.label} ↗
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default function ResultSheet({ primary, secondary, lines, assumptions, explain }: Props) {
  const locale = useLocale()
  const [system] = useUnitSystem()
  // Collapsed by default — the explainer is opt-in via the "How we got this
  // number" toggle in the top-right of the result card, so it doesn't push the
  // primary buy list down on first paint.
  const [explainOpen, setExplainOpen] = useState(false)
  // Track items the user has manually pruned from THIS render of the buy list.
  // Reset every time the calculator emits a fresh `lines` array (input change)
  // — keeps deletion non-destructive: the option always comes back if you
  // tweak any input. For permanent curation, the user saves the sheet and
  // edits it in My Sheets where deletes persist.
  const [removed, setRemoved] = useState<Set<number>>(new Set())
  useEffect(() => { setRemoved(new Set()) }, [lines])
  const removeLine = (idx: number) =>
    setRemoved((prev) => {
      const next = new Set(prev)
      next.add(idx)
      return next
    })

  // Carry the original index alongside each line so the × button can target
  // the right entry regardless of which kind-bucket (consumable/tool) it ends
  // up in. Both buckets filter from the same `removed` set.
  const indexed = lines.map((line, idx) => ({ line, idx })).filter(({ idx }) => !removed.has(idx))
  // Drop any consumable that merely repeats the `secondary` headline item — Paint
  // lists primer as BOTH the secondary result AND a line, which showed primer
  // twice. Keep it in the headline (with the paint) and show accessories only
  // below. Matched by affiliate query, same key the "Where to buy" dedupe uses.
  const consumables = indexed.filter(({ line }) => line.kind === 'consumable' && line.item !== secondary?.query)
  const tools = indexed.filter(({ line }) => line.kind === 'tool')
  // Convert headline qty + unit when the user selects metric. Discrete units
  // (bundle, sheet, piece) pass through unchanged via the same helper.
  const primaryShown = isImperialMeasurement(primary.unit)
    ? convertQty(primary.qty, primary.unit, system)
    : { qty: primary.qty, unit: primary.unit }
  const secondaryShown = secondary
    ? (isImperialMeasurement(secondary.unit)
        ? convertQty(secondary.qty, secondary.unit, system)
        : { qty: secondary.qty, unit: secondary.unit })
    : null
  // Some calculators (e.g. Paint) declare primer as BOTH the `secondary`
  // result and a `consumable` line, which double-listed it in "Where to buy".
  // Dedupe by affiliate query string — same query = same destination URL.
  // Also honor the local `removed` set so pruned lines drop out of the shopping
  // section too — consistent UX with the list above.
  const allItems = [
    { label: primary.name, query: primary.query },
    ...(secondary && secondary.qty > 0 ? [{ label: secondary.name, query: secondary.query }] : []),
    ...lines.map((l, idx) => ({ label: l.label, query: l.item, _idx: idx })).filter((l) => !removed.has(l._idx)),
  ]
  const seen = new Set<string>()
  const buyItems = allItems.filter((it) => {
    const key = it.query
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-surface shadow-sm" aria-live="polite">
      {/* Headline answer + optional "How we got this number" toggle */}
      <div className="border-b border-border bg-brand-tint/60 p-5">
        {/* Header row: the section label on the left, and a clearly-labelled
            "How we got this number" toggle on the right — replaces the old
            cryptic ⓘ icon, which users didn't know to click. */}
        <div className="flex items-start justify-between gap-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">{t(locale, 'result.youllNeed')}</div>
          {explain && (
            <button
              type="button"
              onClick={() => setExplainOpen((o) => !o)}
              aria-expanded={explainOpen}
              className="no-print -mt-1 inline-flex shrink-0 items-center gap-1 rounded-md border border-brand/40 bg-surface px-2.5 py-1 text-xs font-medium text-brand-text shadow-sm transition-colors hover:bg-brand/10"
            >
              {t(locale, 'result.explainAria')}
              <span aria-hidden className="text-[9px]">{explainOpen ? '▲' : '▼'}</span>
            </button>
          )}
        </div>
        <div className="tnum mt-1 text-result font-bold text-text">
          {fmtNum(primaryShown.qty, 2)} <span className="text-2xl font-semibold">{unitLabel(primaryShown.unit, primaryShown.qty, locale)}</span>
        </div>
        {/* What to actually buy — bulleted, dark, and larger so it's the first
            thing the eye lands on after the headline number. (Was small gray
            text users skimmed past.) */}
        <ul className="mt-2 space-y-1">
          <li className="flex gap-2 text-base text-text">
            <span aria-hidden className="select-none text-brand-text">•</span>
            <span>{primary.label}</span>
          </li>
          {secondary && secondaryShown && secondary.qty > 0 && (
            <li className="tnum flex gap-2 text-base text-text">
              <span aria-hidden className="select-none text-brand-text">•</span>
              <span>
                {fmtNum(secondaryShown.qty, 2)} {unitLabel(secondaryShown.unit, secondaryShown.qty, locale)} {t(locale, 'result.of')} {lowerFirst(secondary.label)}
              </span>
            </li>
          )}
        </ul>

        {/* Calculators with a breakdown panel surface their assumptions through
            the top-right toggle; the rest keep the dense "Based on …" line here
            as their only context. */}
        {!explain && <p className="mt-2 text-xs text-text-subtle">{assumptions}</p>}

        {/* Expanded "How we got this" panel — slide-in inside the headline
            card. Click the ⓘ to toggle. Page-level `explain` JSX provides the
            step-by-step math so the user can verify every assumption. */}
        {explain && explainOpen && (
          <div className="mt-4 rounded-md border border-border bg-surface/80 p-3 text-sm text-text-body">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-subtle">
              {t(locale, 'result.explainTitle')}
            </p>
            {explain}
          </div>
        )}
      </div>

      {/* Materials list — clean spec, quantities only. Each row has a × that
          drops the item from the list and the Where-to-buy section. Deletes
          reset when any input changes (the calculator recomputes new lines). */}
      <div className="p-5">
        <h3 className="text-sm font-semibold text-text">{t(locale, 'result.materialsList')}</h3>
        <div className="mt-1 divide-y divide-border">
          {consumables.map(({ line, idx }) => (
            <Row key={idx} line={line} onRemove={() => removeLine(idx)} />
          ))}
        </div>

        {tools.length > 0 && (
          <>
            <h3 className="mt-5 text-sm font-semibold text-text">{t(locale, 'result.toolsYoullWant')}</h3>
            <p className="text-xs text-text-subtle">{t(locale, 'result.buyOnce')}</p>
            <div className="mt-1 divide-y divide-border">
              {tools.map(({ line, idx }) => (
                <Row key={idx} line={line} onRemove={() => removeLine(idx)} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Actions row (Save/Print/PDF/Share) is rendered by the parent page
          via <CalcActions> in the LEFT (form) column. That column is wider, so
          all four buttons fit on a single line; in this narrower right column
          the Share button used to wrap to a second row. */}

      {/* Single consolidated shopping section, at the very end */}
      <WhereToBuy items={buyItems} />

      <p className="px-4 pb-4 pt-3 text-center text-[11px] text-text-subtle">
        {t(locale, 'result.estimatesOnly')}
      </p>
    </section>
  )
}
